import { Tooltip } from '@platform/ui-library';
import { Fragment } from 'react';
import { ChannelSettingSelector, LanguageSettingSelector } from '@app/page-components';
import { useGlobalSettings } from '@app/stores';
import {
  defaultTtsVoice,
  DesignChannelType,
  DesignLanguage,
  LanguageCode,
  PromptAudioUrls
} from '@app/types';
import {
  findLanguageEntry,
  getPromptText,
  isTextDynamic
} from '@app/utilities';
import { LanguageLabel } from '../LanguageLabel';
import { PromptTextarea } from '../PromptTextarea';
import { PromptAudioController } from './prompt-audio-controller';
import styles from './PromptTextAreas.module.scss';

export type PromptTextAreaProps = {
  context: any;
  onUrlChange: (newValue: PromptAudioUrls | null) => void;
};

type UniqueLanguages = (DesignLanguage & {
  count: number;
})[];

export function PromptTextAreas({ context: node, onUrlChange }: PromptTextAreaProps) {
  const { serialized } = window.apm;
  const currentCachedModule = window.apm.getCurrentDiagramWidget().props.node;
  const currentModule = serialized.components.modules.find((m) => m.id === currentCachedModule.id);
  const { channel, ttsVoices, voice } = useGlobalSettings();
  const { isOmniChannel } = serialized.omniChannel;

  let usedLanguages: LanguageCode[] = [];

  const languages = serialized.languages.reduce((acc, language) => {
    if (usedLanguages.includes(language.language)) {
      return acc;
    }
    usedLanguages.push(language.language);
    return [
      ...acc,
      {
        ...language,
        count: serialized.languages.filter(({ language: languageCode }) => languageCode === language.language).length
      }
    ];
  }, [] as UniqueLanguages);

  return (
    <>
      {languages.map((language) => {
        const {
          count,
          language: languageCode,
          ttsFallBackVoice
        } = language;
        const multipleTtsPerLanguage = count > 1;
        const ttsVoice = (multipleTtsPerLanguage) ? (
          ttsVoices[languageCode] ?? defaultTtsVoice
        ) : ttsFallBackVoice;
        const needsTtsPick = !ttsVoice || ttsVoice === defaultTtsVoice;
        const selectedLanguage = (
          (multipleTtsPerLanguage && !!ttsVoice && ttsVoice !== defaultTtsVoice) ? (
            findLanguageEntry(serialized.languages, {
              languageCode,
              ttsVoice,
              voice
            })
          ) : null
        ) ?? language;
        const isDynamicPrompt = isTextDynamic({
          languageCode,
          node,
          serialized,
          ttsVoice
        });
        const isAudioLocked = !!(
          !isDynamicPrompt
          && ttsVoice
          && node?.props?.config?.audioUrls?.[languageCode]?.[ttsVoice]?.lockedUrl
        );
        const promptText = getPromptText({ languageCode, node, ttsVoice });
        const headerTooltip = (isAudioLocked) ? (
          'Unlock the recording to edit the text.'
        ) : (needsTtsPick) ? (
          'Select a voice to fetch recordings and generate audio.'
        ) : (!promptText.trim()) ? (
          'Enter prompt text to generate audio.'
        ) : (isDynamicPrompt) ? (
          'A preview can be heard, but dynamic prompts cannot be locked in as approved.'
        ) : null;

        return (
          <Fragment key={languageCode}>
            <div className={styles.labelWrapper}>
              <label>
                <LanguageLabel
                  includeTts={!multipleTtsPerLanguage}
                  includeVt={!multipleTtsPerLanguage}
                  language={(multipleTtsPerLanguage) ? language : selectedLanguage}
                />
              </label>
              {(!multipleTtsPerLanguage) ? null : (
                <div
                  className={styles.optionsWrapper}
                  data-options
                >
                  <LanguageSettingSelector
                    includeLanguage={false}
                    languageCode={languageCode}
                    onChange={node.update}
                  />
                </div>
              )}
              {(!headerTooltip) ? null : (
                <Tooltip
                  className={styles.infoIcon}
                  fixedWidth={150}
                  horizontalAlign="right"
                  invert
                  style={{ alignSelf: 'center' }}
                  tooltip={headerTooltip}
                />
              )}
            </div>
            <PromptTextarea
              {...{
                context: node,
                hideLabel: true,
                isPropertyPanel: true,
                lang: languageCode,
                readOnly: isAudioLocked,
                valKey: 'text',
                value: { [languageCode]: getPromptText({ languageCode, node }) }
              }}
            />
            {(channel !== DesignChannelType.voice) ? null : (
              <PromptAudioController
                disabled={needsTtsPick}
                language={selectedLanguage}
                node={node}
                nodeModule={currentModule!}
                onUrlChange={onUrlChange}
                ttsVoice={ttsVoice}
              />
            )}
          </Fragment>
        );
      })}
      {(languages.length < 1) ? null : (
        <hr className={styles.languagesDivider} />
      )}
      {!isOmniChannel ? null : (
        <div className="form-component-container">
          <label>Channel Layer</label>
          <ChannelSettingSelector onChange={node.update} />
        </div>
      )}
    </>
  );
}
